import * as _ from 'lodash';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import useAsyncStatus from '../../../../hooks/useAsyncStatus';
import useOnMount from '../../../../hooks/useOnMount';
import * as api from '../../../../redux/api';
import Loader from '../../../generic/Loader';
import Text from '../../../generic/Text';
import type { NativeFileDetectedResult } from '../../../../../typings/api';
import type { FailedFetchResponse } from '../../../../helpers/errors';

type NativeCodeDescriptionProps = {
  locator: string;
};

const DetectionReason: VoidFunctionComponent<{fileInfo: NativeFileDetectedResult}> = ({
  fileInfo,
}) => {
  let reasonDescription: string | null = null;
  let reasonData: string | null = null;
  if (fileInfo.reason.extension) {
    reasonDescription = 'extension';
    reasonData = fileInfo.reason.extension;
  }

  if (fileInfo.reason.detectedFileType) {
    reasonDescription = 'filetype';
    reasonData = fileInfo.reason.detectedFileType;
  }

  if (fileInfo.reason.mimeType) {
    reasonDescription = 'mimetype';
    reasonData = fileInfo.reason.mimeType;
  }

  if (!reasonDescription) {
    return null;
  }

  return (
    <ul>
      <li>
        Suspicious {reasonDescription} detected:
        <span className="padding-left-sm"><Text.Monospace>{reasonData}</Text.Monospace></span>
      </li>
    </ul>
  );
};

const FileInfoList: VoidFunctionComponent<{fileList: NativeFileDetectedResult[]}> = ({ fileList }) => {
  if (_.isEmpty(fileList)) {
    return null;
  }

  return (
    <div>
      The following files were detected:
      <ul className="padding-top-sm">
        {fileList.map((fileInfo) => (
          <li key={fileInfo.filename}>
            <Text.Monospace>{fileInfo.filename}</Text.Monospace>
            <DetectionReason fileInfo={fileInfo} />
          </li>))}
      </ul>
    </div>
  );
};

/**
 * @todo Move this over to issue drawer once we delete Issues v1 FE.
 */
const NativeCodeDescription: VoidFunctionComponent<NativeCodeDescriptionProps> = ({
  locator,
}) => {
  const [fileList, setFileList] = useState<NativeFileDetectedResult[]>([]);
  const { status, onError, onPending, onSuccess } = useAsyncStatus();

  useOnMount(async () => {
    onPending();
    try {
      const { data } = await api.get<NativeFileDetectedResult[]>(`/api/revisions/${locator}/nativeCodeFiles`);
      setFileList(data ?? []);
      onSuccess();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  });

  return (
    <>
      <div>
        This package contains compiled executables that are not normally expected within this language/ecosystem.
      </div>
      {!status.isLoading ? <FileInfoList fileList={fileList} /> : <Loader />}
    </>
  );
};

export default NativeCodeDescription;
